import React, { Component } from 'react';
import { bool, func, node, number, object, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { ExpandingTextarea, FieldHelp, Icon } from '../../components';
import css from './FieldTextInput.module.css';

const CONTENT_MAX_LENGTH = 5000;

/**
 * A text field from the Components board: label (with "Optional" when `optional`), the input or
 * textarea, and one line under it: `helpText`, or the error instead of it, or `successText` once
 * a value is valid. `locked` disables it with a lock (put the reason in `helpText`). A textarea
 * with `maxLength` counts its characters ("78 / 500").
 */
class FieldTextInputComponent extends Component {
  render() {
    /* eslint-disable no-unused-vars */
    const {
      rootClassName,
      className,
      inputRootClass,
      helpClassName,
      customErrorText,
      id,
      label,
      helpText,
      successText,
      optional,
      locked,
      maxLength: maxLengthProp,
      input,
      meta,
      onUnmount,
      isUncontrolled,
      inputRef,
      ...rest
    } = this.props;
    /* eslint-enable no-unused-vars */

    if (label && !id) {
      throw new Error('id required when a label is given');
    }

    const { valid, invalid, touched, error } = meta;
    const isTextarea = input.type === 'textarea';
    const errorText = customErrorText || error;

    // Errors show once the field has been touched and the validation has failed.
    const hasError = !!customErrorText || !!(touched && invalid && error);
    const showSuccess = !hasError && !!successText && touched && valid && !!input.value;
    const disabled = locked || rest.disabled;

    const helpId = id ? `${id}-help` : null;
    const hasMessage = hasError || showSuccess || !!helpText;
    const counter =
      isTextarea && maxLengthProp ? `${(input.value || '').length} / ${maxLengthProp}` : null;

    // Textarea doesn't need type.
    const { type, ...inputWithoutType } = input;
    // Uncontrolled input uses defaultValue instead of value.
    const { value: defaultValue, ...inputWithoutValue } = input;
    // Use inputRef if it is passed as prop.
    const refMaybe = inputRef ? { ref: inputRef } : {};

    const status = hasError ? 'error' : showSuccess ? 'success' : locked ? 'locked' : null;
    const inputClasses =
      inputRootClass ||
      classNames(css.input, {
        [css.inputSuccess]: showSuccess,
        [css.inputError]: hasError,
        [css.withStatus]: !!status,
        [css.textarea]: isTextarea,
      });

    const a11yProps = {
      'aria-invalid': hasError ? 'true' : undefined,
      'aria-describedby': hasMessage && helpId ? helpId : undefined,
    };

    const inputProps = isTextarea
      ? {
          className: inputClasses,
          id,
          rows: 1,
          maxLength: maxLengthProp || CONTENT_MAX_LENGTH,
          ...refMaybe,
          ...inputWithoutType,
          ...rest,
          ...a11yProps,
          disabled,
        }
      : isUncontrolled
      ? {
          className: inputClasses,
          id,
          type,
          defaultValue,
          maxLength: maxLengthProp || undefined,
          ...refMaybe,
          ...inputWithoutValue,
          ...rest,
          ...a11yProps,
          disabled,
        }
      : {
          className: inputClasses,
          id,
          type,
          maxLength: maxLengthProp || undefined,
          ...refMaybe,
          ...input,
          ...rest,
          ...a11yProps,
          disabled,
        };

    const statusIcon =
      status === 'error' ? (
        <Icon name="error" size={16} solid className={classNames(css.status, css.statusError)} />
      ) : status === 'success' ? (
        <Icon
          name="success"
          size={16}
          solid
          className={classNames(css.status, css.statusSuccess)}
        />
      ) : status === 'locked' ? (
        <Icon name="lock" size={14} className={classNames(css.status, css.statusLocked)} />
      ) : null;

    const classes = classNames(rootClassName || css.root, className);
    return (
      <div className={classes}>
        {label ? (
          <label htmlFor={id} className={css.label}>
            {label}
            {optional ? (
              <span className={css.optional}>
                <FormattedMessage id="FieldTextInput.optional" />
              </span>
            ) : null}
          </label>
        ) : null}
        <div className={classNames(css.control, { [css.controlTextarea]: isTextarea })}>
          {isTextarea ? <ExpandingTextarea {...inputProps} /> : <input {...inputProps} />}
          {statusIcon}
        </div>
        <FieldHelp
          id={helpId}
          className={helpClassName}
          helpText={helpText}
          errorText={hasError ? errorText : null}
          successText={showSuccess ? successText : null}
          counter={counter}
        />
      </div>
    );
  }
}

FieldTextInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputRootClass: null,
  helpClassName: null,
  onUnmount: null,
  customErrorText: null,
  id: null,
  label: null,
  helpText: null,
  successText: null,
  optional: false,
  locked: false,
  maxLength: null,
  isUncontrolled: false,
  inputRef: null,
};

FieldTextInputComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,
  // Added to the line under the field (e.g. its colours on a dark surface)
  helpClassName: string,
  onUnmount: func,

  // Error message that can be manually passed to input field,
  // overrides default validation message
  customErrorText: string,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: node,

  // The line under the field: help, or a note once the value is valid
  helpText: node,
  successText: node,
  // Adds "Optional" next to the label
  optional: bool,
  // Disabled with a lock; say why in helpText
  locked: bool,
  // A textarea with maxLength shows a character counter
  maxLength: number,

  // Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
  // https://reactjs.org/docs/uncontrolled-components.html#default-values
  isUncontrolled: bool,
  // a ref object passed for input element.
  inputRef: object,

  // Generated by final-form's Field component
  input: shape({
    onChange: func.isRequired,
    // Either 'textarea' or something that is passed to the input element
    type: string.isRequired,
  }).isRequired,
  meta: object.isRequired,
};

class FieldTextInput extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldTextInputComponent} {...this.props} />;
  }
}

export default FieldTextInput;
